// Recover from a missing webpack (or ESM) async chunk without looping.
//
// A ChunkLoadError is NOT an app bug — the JS file the tab asked for is gone
// (dev HMR rebuilt the graph, or a deploy replaced hashed filenames). The
// recovery is: retry the import once, then reload onto a fresh index.html.
//
// LOOP RULE (the new-bug we must not ship): reload AT MOST once per 15s
// window, latched in sessionStorage. If we cannot write the latch, we do
// NOT reload — the existing ErrorBoundary "Refresh" screen is safer than
// an unlatched reload loop. A second failure inside the window also does
// not reload (broken deploy of that chunk); the user sees Refresh.

export const CHUNK_RELOAD_WINDOW_MS = 15 * 1000;
export const CHUNK_RELOAD_STORAGE_KEY = 'crewvex.chunkReloadAt';

function chunkLoadText(value) {
  return String(value || '');
}

function matchesChunkLoadText(text) {
  return (
    text.includes('Loading chunk') ||
    text.includes('Loading CSS chunk') ||
    text.includes('Failed to fetch dynamically imported module') ||
    text.includes('Importing a module script failed')
  );
}

/** True for webpack ChunkLoadError and the equivalent ESM dynamic-import failures. */
export function isChunkLoadError(error) {
  if (error == null) return false;
  if (typeof error === 'string') return matchesChunkLoadText(error);
  const name = error.name || '';
  const msg = chunkLoadText(error.message || error);
  return name === 'ChunkLoadError' || matchesChunkLoadText(msg);
}

/** True if we already attempted a recovery reload in this tab recently. */
export function chunkReloadRecentlyAttempted() {
  if (typeof window === 'undefined') return false;
  // Accessing sessionStorage can throw (Safari "Block all cookies", sandboxed
  // iframes). This runs from ErrorBoundary.getDerivedStateFromError — a throw
  // there unmounts the whole tree. Keep EVERY storage touch inside the try.
  try {
    const storage = window.sessionStorage;
    if (!storage) return false;
    const last = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY) || '0');
    return Boolean(last && Date.now() - last < CHUNK_RELOAD_WINDOW_MS);
  } catch {
    return false;
  }
}

function navigateToFreshCopy() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Reload once onto a cache-busted URL. Returns true if a reload was started.
 * Returns false if a reload already happened in the window, or if we cannot
 * persist the latch (refuse to loop).
 */
export function recoverFromChunkLoadError() {
  if (typeof window === 'undefined') return false;
  if (chunkReloadRecentlyAttempted()) return false;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
  } catch {
    // No latch → no reload. ErrorBoundary / Refresh stays the fallback.
    return false;
  }
  navigateToFreshCopy();
  return true;
}

/**
 * Run a dynamic import, retry once on ChunkLoadError, then recover via reload.
 * If a reload starts, the returned promise never settles (the page is leaving).
 */
export function importWithChunkRetry(importer) {
  return importer().catch((err) => {
    if (!isChunkLoadError(err)) throw err;
    return importer().catch((err2) => {
      if (isChunkLoadError(err2) && recoverFromChunkLoadError()) {
        // Page is navigating away. If replace/reload no-ops, reject so the
        // ErrorBoundary can show Refresh instead of hanging Suspense forever.
        return new Promise((_, reject) => {
          setTimeout(() => reject(err2), 3500);
        });
      }
      throw err2;
    });
  });
}
